import React from 'react';

import Constants from '../../../Constants';
import {
  CONNECTION_CATEGORY_TEMPLATIZED_EMAIL_SCRIPT,
  CONNECTION_CATEGORY_WEBHOOK,
  CONNECTION_CATEGORY_SYSLOG,
} from 'js/constants/connectionCategories';
import { ROUTE_MY_DATA_SETS } from 'js/constants/pathnames';
import { RESOURCE_TYPE_SOURCE } from 'js/constants/resourceTypes';
import { ACLQueryModeTypes, SearchFilterTypes } from 'js/constants/visibilityFilters';
import DateUtils from '../../../lib/DateUtils';
import { bytesToString, formatCode, formatNumber } from 'js/lib/FormatUtils';
import ConfigurationViewContainer from '../../elements/configViews/ConfigurationViewContainer';
import TagsEditor from '../../elements/tags/TagsEditor';
import TagsView from '../../elements/tags/TagsView';

import { has, isEmpty, map } from 'lodash';
import { Link } from 'react-router-dom';
import RoutePrefixContext from '../../../../new/contexts/RoutePrefixContext';
import { SwitchableSection } from 'new/elements/SwitchableSection/SwitchableSection';
import { JSONView } from 'new/elements/JSONView/JSONView';
import { MetricsPanel } from '@modules/monitor/resource-triage/components/MetricsPanel';
import {
  getPresetDateRange,
  formatDateRangeForApi,
} from '@modules/monitor/resource-triage/components/MetricsTrendPanel';
import { Stack } from 'new/elements/Stack';
import {
  ROUTE_MY_NEXSETS,
  ROUTE_DESTINATIONS,
  ROUTE_API_KEYS,
  ROUTE_SETTINGS_AUTHENTICATION,
  ROUTE_CONNECT,
} from 'new/constants/pathnames';
import QueryPromptConfiguration from 'js/components/data_sources/cards/configViews/QueryPromptConfiguration';
import SyslogConfigView from './configViews/SyslogConfigView';
import ApiServerConfigView from './configViews/ApiServerConfigView';
import { shouldHideRecordsForResource } from 'js/lib/PipelineUtils';
import { CredentialWidgetFromProps } from 'new/elements/CredentialWidget';

const routesByType = {
  data_sink: ROUTE_DESTINATIONS,
  data_set: ROUTE_MY_NEXSETS,
};

class DataSourceOverviewTab extends React.Component {
  static contextType = RoutePrefixContext;

  constructor(props) {
    super(props);
    this.state = {
      metricsRange: getPresetDateRange('30d'),
    };
  }

  componentDidMount() {
    this.props.loadDataSource(this.props.data.id, {
      aclQueryMode: ACLQueryModeTypes.MODE_COLLABORATOR.key,
    });

    const vendor = this.props.vendors[this.props.data?.vendor_id];
    if (vendor?.name) {
      this.props.loadFullVendor(vendor.name);
    }

    this._loadResourceMetrics();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.data.id !== this.props.data.id) {
      this._loadResourceMetrics();
    }
  }

  _loadResourceMetrics = () => {
    this._loadResourceMetricsAggregate();
    this.props.fetchResourceMetricsPerDay(
      this.props.data.id,
      RESOURCE_TYPE_SOURCE,
      formatDateRangeForApi(this.state.metricsRange),
    );
  };

  _loadResourceMetricsAggregate = () => {
    this.props.fetchResourceMetricsAggregate(this.props.data.id, RESOURCE_TYPE_SOURCE);
  };

  onMetricsDatesChanged = (dates) => {
    this.setState({
      metricsRange: { start: new Date(dates.start), end: new Date(dates.end) },
    });
    this.props.fetchResourceMetricsPerDay(
      this.props.data.id,
      RESOURCE_TYPE_SOURCE,
      formatDateRangeForApi(dates),
    );
  };

  render() {
    const {
      data,
      apiKeys,
      summaryData,
      metrics,
      credentials,
      connectionCategory,
      resourceTypeFields,
      currentOrg,
      readOnly,
      vendors,
      vendorEndpoints,
      vendorEndpointParams,
    } = this.props;

    const filteredFields = data?.flow_triggers?.length
      ? resourceTypeFields.filter((f) => f.name !== 'start.cron')
      : resourceTypeFields;

    let createdDate = DateUtils.convertDateStringToFormat(data.created_at);
    let lastUpdate = DateUtils.convertDateStringToFormat(data.updated_at);
    let setsCount = has(summaryData, 'data_sets') ? summaryData.data_sets : null;

    let aggregateMetrics =
      metrics && has(metrics, 'agg') && has(metrics.agg, 'metrics') ? metrics.agg.metrics : null;
    let metricsByDate =
      metrics && has(metrics, 'byDate') && !isEmpty(metrics.byDate) ? metrics.byDate : null;

    let totalVolume = '-',
      numRecords = '-',
      numberErrors = '-';

    if (!this.props.isLoading.aggMetrics) {
      totalVolume = !isEmpty(aggregateMetrics)
        ? bytesToString(aggregateMetrics.size)
        : Constants.DATA_NOT_AVAILABLE;

      numRecords = !isEmpty(aggregateMetrics)
        ? formatNumber(aggregateMetrics.records)
        : Constants.DATA_NOT_AVAILABLE;

      numberErrors = !isEmpty(aggregateMetrics)
        ? formatNumber(aggregateMetrics.errors)
        : Constants.DATA_NOT_AVAILABLE;
    }

    const isWebhookConnection = connectionCategory === CONNECTION_CATEGORY_WEBHOOK;

    const isSyslogSource = connectionCategory === CONNECTION_CATEGORY_SYSLOG;

    let apiKeyDisplay =
      isWebhookConnection && !isEmpty(apiKeys) ? map(apiKeys, (k) => k.api_key) : null;

    const credentialsContent = (() => {
      if (isWebhookConnection) return apiKeyDisplay;
      if (credentials) {
        return <CredentialWidgetFromProps credential={credentials} />;
      }
      return null;
    })();

    let sourceConfig =
      connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_EMAIL_SCRIPT
        ? data.script_config || {}
        : data.source_config || {};

    const { source_config, template_config = {} } = data;
    const configurations = Object.keys(template_config).length
      ? { source_config, template_config }
      : { source_config };
    const configurationsJSON = JSON.stringify(configurations);

    const tagsView =
      readOnly === true ? (
        <TagsView data={this.props.data} />
      ) : (
        <TagsEditor id={data.id} resourceType={RESOURCE_TYPE_SOURCE} />
      );

    const isQueryPrompt = data.node_type === 'query_prompt';
    const isApiServer = data.node_type === 'api_server';

    // check if we hide records for this flow type
    const hideRecords = shouldHideRecordsForResource(data);

    const lifetimeMetrics = {
      records: numRecords,
      volume: totalVolume,
      errors: numberErrors,
    };

    let configView = null;
    if (isQueryPrompt) {
      configView = <QueryPromptConfiguration data={data} />;
    } else if (isSyslogSource) {
      configView = <SyslogConfigView data={data} />;
    } else if (isApiServer) {
      configView = <ApiServerConfigView data={data} />;
    } else {
      configView = (
        <SwitchableSection
          switchTitle="JSON view"
          title="Source Configuration"
          DefaultComponent={
            <ConfigurationViewContainer
              entityType={RESOURCE_TYPE_SOURCE}
              connectionCategory={connectionCategory}
              config={sourceConfig}
              configFields={filteredFields}
              resourceId={data.id}
              creds={credentialsContent}
              currentOrg={currentOrg}
              data={data}
              vendors={vendors}
              vendorEndpoints={vendorEndpoints}
              vendorEndpointParams={vendorEndpointParams}
            />
          }
          SwitchableComponent={
            <div style={{ height: 350 }}>
              <JSONView json={formatCode(configurationsJSON, 'application/json')} />
            </div>
          }
        />
      );
    }

    return (
      <div className="tab-content">
        <section>
          <div className="detail-row">
            <div className="detail-col">
              <div className="detail-key">Source ID</div>
              <div className="detail-value">{data.id}</div>
              <div className="detail-key">Created On</div>
              <div className="detail-value">{createdDate}</div>
              {!isWebhookConnection && credentialsContent ? (
                <div>
                  <div className="detail-key">Credentials</div>
                  <div className="detail-value">{credentialsContent}</div>
                </div>
              ) : null}
            </div>

            <div className="detail-col">
              <div className="detail-key">
                {isQueryPrompt
                  ? 'Api Keys'
                  : isApiServer
                    ? 'Requests Count'
                    : 'Detected Nexsets Count'}
              </div>
              <div className="detail-value">
                {isQueryPrompt ? (
                  apiKeys?.map((api) => {
                    return (
                      <Link
                        to={`${ROUTE_SETTINGS_AUTHENTICATION}?search=${SearchFilterTypes.ID}:${api.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {api.id}
                      </Link>
                    );
                  })
                ) : setsCount && setsCount > 0 ? (
                  <Link
                    to={`${this.context}${ROUTE_MY_DATA_SETS}?search=${SearchFilterTypes.SOURCE_ID}:${data.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {setsCount}
                  </Link>
                ) : (
                  setsCount
                )}
              </div>

              <div className="detail-key">Last Updated On</div>
              <div className="detail-value">{lastUpdate}</div>
            </div>
          </div>
          {data?.flow_triggers?.length ? (
            <div className="detail-row">
              <div className="detail-col">
                <div className="detail-key">Resource Triggers</div>
                <Stack>
                  {data.flow_triggers.map((trigger) => {
                    return (
                      <div className="detail-value">
                        <Link
                          to={`${routesByType[trigger.triggering_resource_type]}?search=${SearchFilterTypes.ID}:${trigger.triggering_resource_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {trigger.triggering_resource_id}
                        </Link>
                      </div>
                    );
                  })}
                </Stack>
              </div>
            </div>
          ) : null}
        </section>
        <hr />

        <section>{configView}</section>

        <hr />

        <section>
          <div className="detail-row single-col">
            <div className="detail-col">
              <div className="detail-key">Tags</div>
              <div className="detail-value">{tagsView}</div>
            </div>
          </div>
        </section>

        <hr />

        {isQueryPrompt || isSyslogSource || isApiServer ? null : (
          <section>
            <MetricsPanel
              lifetimeMetrics={lifetimeMetrics}
              trendData={metricsByDate}
              start={this.state.metricsRange.start}
              end={this.state.metricsRange.end}
              onDatesChanged={this.onMetricsDatesChanged}
              onRefresh={this._loadResourceMetricsAggregate}
              hideRecords={hideRecords}
              loading={this.props.isLoading.aggMetrics || this.props.isLoading.dayMetrics}
            />
          </section>
        )}
      </div>
    );
  }
}

export default DataSourceOverviewTab;
